import { RedirectToSignIn, useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactElement, ReactNode } from "react";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import { isAlwaysAiAdmin } from "@/lib/permissions";
import type { AlwaysAiUser } from "../../../../shared/api-types";

/**
 * Port of the legacy admin page auth surface (apps/always-ai
 * src/app/admin/layout.tsx + the per-page requireV2Auth/requireAdmin guard):
 * signed-out visitors are redirected to sign-in, and auth failures (deactivated
 * account, non-admin role) render the full-page centered "{status} — {message}"
 * surface the legacy pages return for AuthError.
 */
export function AdminGuard({ children }: { children: ReactNode }): ReactElement | null {
  const { isLoaded, isSignedIn, getToken } = useAuth({
    treatPendingAsSignedOut: false,
  });
  const me = useQuery({
    queryKey: ["current-user"],
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
    enabled: isLoaded && isSignedIn === true,
    retry: false,
  });

  if (!isLoaded) return null;

  if (!isSignedIn) return <RedirectToSignIn />;

  if (me.isError) {
    const error = me.error;

    if (error instanceof ApiRequestError) {
      if (error.status === 401) return <RedirectToSignIn />;

      return (
        <AdminAuthErrorPage
          status={error.status}
          message={apiErrorBodyMessage(error.body) ?? "Unable to load your account"}
        />
      );
    }

    return <AdminAuthErrorPage status={500} message="Unable to load your account" />;
  }

  if (!me.data) return null;

  if (!isAlwaysAiAdmin(me.data.user)) {
    return <AdminAuthErrorPage status={403} message="Admin role required" />;
  }

  return <>{children}</>;
}

function AdminAuthErrorPage({
  status,
  message,
}: {
  status: number;
  message: string;
}): ReactElement {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <p className="text-lg text-muted-foreground">
        {status} — {message}
      </p>
    </div>
  );
}

function apiErrorBodyMessage(body: unknown): string | null {
  if (
    body &&
    typeof body === "object" &&
    typeof (body as { error?: unknown }).error === "string"
  ) {
    return (body as { error: string }).error;
  }

  return null;
}

const ADMIN_LINK_CLASS =
  "text-body text-muted-foreground hover:text-foreground underline-offset-4 hover:underline";
const ADMIN_LINKS = [
  { to: "/admin/rds", label: "RDs" },
  { to: "/admin/coverage", label: "Coverage" },
  { to: "/admin/usage", label: "Usage" },
  { to: "/admin/starter-prompts", label: "Starter prompts" },
  { to: "/admin/migrate", label: "Migrate" },
  { to: "/admin/soap-workbench", label: "SOAP workbench" },
  { to: "/admin/scorecard-workbench", label: "Scorecard workbench" },
] as const;

/**
 * Cross-links between the admin surfaces, styled like the legacy right-aligned
 * cross-link set. The Dev link is additionally gated server-side: /api/dev
 * 404s in production.
 */
export function AdminNav(): ReactElement {
  return (
    <nav aria-label="Admin" className="mb-5 flex flex-wrap items-center gap-3">
      {ADMIN_LINKS.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className={ADMIN_LINK_CLASS}
          activeProps={{ className: "font-semibold text-foreground no-underline" }}
        >
          {link.label}
        </Link>
      ))}
      {import.meta.env.MODE !== "production" ? (
        <Link
          to="/admin/dev"
          className={ADMIN_LINK_CLASS}
          activeProps={{ className: "font-semibold text-foreground no-underline" }}
        >
          Dev
        </Link>
      ) : null}
    </nav>
  );
}

export function DataTable({
  rows,
}: {
  rows: Array<Record<string, string | number | null>>;
}): ReactElement {
  if (!rows.length) return <p className="mt-4 text-muted-foreground">No data.</p>;

  const columns = Object.keys(rows[0]);

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr>
            {columns.map((column) => (
              <th className="border-b border-border px-2 py-2 capitalize" key={column}>
                {column.replace(/([A-Z])/g, " $1")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {columns.map((column) => (
                <td className="border-b border-sand-100 px-2 py-2" key={column}>
                  {String(row[column] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
